import { Box, Typography } from "@mui/material";
import type { Table as ArrowTable } from "apache-arrow";
import { DatePivotParams } from "../../../entities/date_pivot_params";
import { PivotControl } from "../../../constants/pivot_control_options";
import { PivotViewConfig } from "../../../entities/pivot_view_config";
import {
  exportCustomViewCSV,
  checkIfViewHasExpandableRows,
} from "./download_component";
import { ControlsHeader } from "./controls_header";
import {
  ControlsSidePanel,
  getActiveSelections,
  hasSidePanelControls,
} from "./side_panel";
import { FilterBy } from "../../filter_by";
import { DateHierarchy } from "../../date_hierarchy";
import { SegmentSelect } from "../../segment_select";
import { Measure } from "../../measure";
import { DatePeriodPicker } from "../../date_period_picker";
import { IncludeAtomicPurchasePlan } from "./include_atomic_purchase_plan";
import { EditModeToggle } from "./edit_mode_toggle";
import { CONTROL_LABEL_SX } from "../../../utils/control_label";
import { createPortal } from "react-dom";
import { useViewActionsSlot } from "../../../hooks/use_view_actions_slot";
import dayjs from "dayjs";
import { useAdmin } from "../../../contexts/app/provider";
import { useModal } from "../../../contexts/modal/hooks";
import { useContext, useCallback, useState } from "react";
import { DemandWalkEditsContext } from "../../../contexts/demand_walk_edits";
import { PurchasingCalendarEditsContext } from "../../../contexts/purchasing_calendar_edits";
import { useActiveEditContext } from "../../../hooks/use_active_edit_context";
import { VersionedDataContext } from "../../../contexts/versioned_data/context";
import { SegmentChangeWarningContent } from "../../modals/segment_change_warning";

export const TransformedCustomViewControlsComponent = <
  T extends DatePivotParams = DatePivotParams,
>({
  viewControllerConfig,
  isLoading,
  measureColumns,
  params,
  segmentColumns,
  setParams,
  dateHierarchyColumns,
  filterColumns,
  columnSchema,
  parsedData,
  isChartView,
  setIsChartView,
  sortConfig,
  setSortConfig,
  onBackendExport,
  inMemoryArrowTable,
  children,
}: {
  viewControllerConfig: PivotViewConfig<T>;
  params: T;
  setParams: (params: T) => void;
  isLoading: boolean;
  dateHierarchyColumns: {
    name: string;
    caption: string;
    min: string;
    max: string;
  }[];
  segmentColumns: { name: string; caption: string }[];
  measureColumns: { name: string; caption: string }[];
  filterColumns: { name: string; caption: string; unique_values: string[] }[];
  columnSchema: Record<
    string,
    {
      dtype: string;
      min?: string;
      max?: string;
      unique_values?: string[];
      value_counts?: Record<string, number>;
    }
  >;
  parsedData?: any;
  isChartView?: boolean;
  setIsChartView?: React.Dispatch<React.SetStateAction<boolean>>;
  sortConfig: Array<{ key: string; direction: "asc" | "desc"; index: number }>;
  setSortConfig: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
  onBackendExport?: (excludeDetailRows: boolean) => Promise<void>;
  inMemoryArrowTable?: ArrowTable | null;
  children?: React.ReactNode;
}) => {
  const { isAdmin } = useAdmin();
  const actionsSlot = useViewActionsSlot();
  const { openModal, closeModal } = useModal();
  const {
    editMode: demandWalkEditMode,
    plannerOverrideInputs,
    finalForecastInputs,
    discardChanges: demandWalkDiscardChanges,
    setEditMode: demandWalkSetEditMode,
  } = useContext(DemandWalkEditsContext);
  const { deliveryDateEdits } = useContext(PurchasingCalendarEditsContext);
  const activeEditContext = useActiveEditContext();
  const editMode = activeEditContext.editMode;
  const discardChanges = activeEditContext.discardChanges;
  const setEditMode = activeEditContext.setEditMode;
  const { mainRun, comparedRun, lastSuccessfulRun } = useContext(VersionedDataContext);
  const activeRun = mainRun ?? lastSuccessfulRun;

  const {
    title,
    showControls,
    excludedFilterColumnFilter,
    datasetName,
    transformId,
  } = viewControllerConfig;
  // Ensure params.filters is always an array
  const safeFilters = Array.isArray(params.filters) ? params.filters : [];
  const userFilters = safeFilters.filter(
    (filter) => !excludedFilterColumnFilter.includes(filter[0])
  );
  const userFilterColumns = filterColumns.filter(
    (filter) => !excludedFilterColumnFilter.includes(filter.name)
  );

  // Show segment change warning modal
  const showSegmentChangeWarning = useCallback(
    (newSegments: string[], oldSegments: string[]) => {
      const handleConfirm = () => {
        discardChanges();
        setParams({ ...params, segments: newSegments });
        setEditMode(true);
        closeModal();
      };

      const handleCancel = () => {
        closeModal();
      };

      openModal({
        disableStepViewer: true,
        maxWidth: "sm",
        disableBackdropClick: true,
        disableEscapeKeyDown: true,
        steps: [
          {
            id: "warning",
            title: "Segment Change Warning",
            content: (
              <SegmentChangeWarningContent
                oldSegments={oldSegments}
                newSegments={newSegments}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
              />
            ),
            disableActions: true,
          },
        ],
      });
    },
    [discardChanges, params, setParams, openModal, closeModal, setEditMode]
  );

  // Handle segment change with edit mode check
  const handleSegmentChange = useCallback(
    (newSegments: string[]) => {
      // Check if there are any edits to warn about (demand walk or purchasing calendar)
      const hasDemandWalkEdits =
        plannerOverrideInputs &&
        (Object.keys(plannerOverrideInputs).length > 0 ||
          Object.keys(finalForecastInputs).length > 0);
      const hasPurchasingCalendarEdits =
        deliveryDateEdits && Object.keys(deliveryDateEdits).length > 0;

      if (editMode && (hasDemandWalkEdits || hasPurchasingCalendarEdits)) {
        showSegmentChangeWarning(newSegments, params.segments || []);
      } else {
        setParams({ ...params, segments: newSegments });
      }
    },
    [
      editMode,
      plannerOverrideInputs,
      finalForecastInputs,
      deliveryDateEdits,
      params,
      setParams,
      showSegmentChangeWarning,
    ]
  );

  const [isExporting, setIsExporting] = useState(false);

  const handleDownload = (excludeDetailRows: boolean = false) => {
    if (
      typeof (viewControllerConfig as any).customHandleDownload === "function"
    ) {
      try {
        // Pass params (which carry the current filters / status) so customer-
        // specific handlers can pick the right transform per active tab.
        (viewControllerConfig as any).customHandleDownload(params);
        return;
      } catch (_) {}
    }

    // Chunked path: delegate to backend CSV export
    if (onBackendExport) {
      setIsExporting(true);
      onBackendExport(excludeDetailRows)
        .catch((err) => {
          console.error("[Export] Backend CSV export failed:", err);
        })
        .finally(() => {
          setIsExporting(false);
        });
      return;
    }

    // In-memory path: use the arrow table directly
    if (!parsedData) return;
    const effectiveArrowTable = parsedData.arrowTable ?? inMemoryArrowTable;
    exportCustomViewCSV(
      effectiveArrowTable,
      params ?? null,
      title,
      columnSchema,
      sortConfig.map(({ key, direction, index }) => ({
        key,
        direction,
        index,
      })),
      viewControllerConfig,
      excludeDetailRows,
      activeRun?.createdAt ?? null,
      comparedRun?.createdAt ?? null
    );
  };

  // Separate gesture from the CSV Download: hands the active params to a
  // customer-specific PDF handler and returns its promise so the button can
  // spin until the (server-rendered) zip resolves.
  const handleDownloadPdf = (): void | Promise<void> => {
    const fn = (viewControllerConfig as any).customHandleDownloadPdf;
    if (typeof fn === "function") {
      return fn(params);
    }
  };

  const CustomPanel = viewControllerConfig.customControlPanel;

  const customControlPanel = CustomPanel ? (
    <CustomPanel
      viewControllerConfig={viewControllerConfig}
      params={params}
      setParams={setParams}
      isLoading={isLoading}
      isChartView={isChartView}
      setIsChartView={setIsChartView}
      sortConfig={sortConfig}
      setSortConfig={setSortConfig}
      dateHierarchyColumns={dateHierarchyColumns}
      segmentColumns={segmentColumns}
      measureColumns={measureColumns}
      filterColumns={userFilterColumns}
      columnSchema={columnSchema}
      parsedData={parsedData}
    />
  ) : null;

  const selections = getActiveSelections({
    params,
    showControls,
    dateHierarchyColumns,
    measureColumns,
    userFilters,
  });

  // The data-shaping controls, stacked vertically as labeled sections for the
  // left rail. Each section is gated by the same showControls flag as before,
  // so a view only renders the sections it declares (no empty scaffolding).
  const sidePanelSections = (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {showControls.includes(PivotControl.DatePeriodPicker) && (
        <Box
          sx={{
            px: 2,
            py: "14px",
            borderBottom: "1px solid",
            borderColor: (t) => t.palette.atomic.borderLight,
          }}
        >
          <Box sx={CONTROL_LABEL_SX}>Date period</Box>
          <DatePeriodPicker
            isLoading={isLoading}
            dateColumn={params.dateHierarchy}
            columns={dateHierarchyColumns}
            minDate={
              dateHierarchyColumns.find(
                (col) => col.name === params.dateHierarchy
              )?.min
                ? dayjs(
                    dateHierarchyColumns.find(
                      (col) => col.name === params.dateHierarchy
                    )?.min
                  )
                : null
            }
            maxDate={
              dateHierarchyColumns.find(
                (col) => col.name === params.dateHierarchy
              )?.max
                ? dayjs(
                    dateHierarchyColumns.find(
                      (col) => col.name === params.dateHierarchy
                    )?.max
                  )
                : null
            }
            startDate={params.minDate ? dayjs(params.minDate) : dayjs()}
            setStartDate={(startDate) =>
              setParams({
                ...params,
                minDate: startDate || params.minDate,
              })
            }
            endDate={params.maxDate ? dayjs(params.maxDate) : dayjs()}
            setEndDate={(endDate) =>
              setParams({ ...params, maxDate: endDate || params.maxDate })
            }
          />
        </Box>
      )}

      {isAdmin &&
        showControls.includes(PivotControl.IncludeAtomicPurchasePlan) && (
          <Box
          sx={{
            px: 2,
            py: "14px",
            borderBottom: "1px solid",
            borderColor: (t) => t.palette.atomic.borderLight,
          }}
        >
            <IncludeAtomicPurchasePlan
              value={(params as any).includeAtomicPurchasePlan ?? false}
              onChange={(value) =>
                setParams({
                  ...params,
                  includeAtomicPurchasePlan: value,
                } as T)
              }
              disabled={isLoading}
            />
          </Box>
        )}

      {showControls.includes(PivotControl.DateHierarchy) && (
        <Box
          sx={{
            px: 2,
            py: "14px",
            borderBottom: "1px solid",
            borderColor: (t) => t.palette.atomic.borderLight,
          }}
        >
          <DateHierarchy
            isLoading={isLoading}
            columns={dateHierarchyColumns}
            column={params.dateHierarchy}
            setColumn={(column) =>
              setParams({ ...params, dateHierarchy: column })
            }
          />
        </Box>
      )}

      {showControls.includes(PivotControl.SegmentSelectMulti) && (
        <Box
          sx={{
            px: 2,
            py: "14px",
            borderBottom: "1px solid",
            borderColor: (t) => t.palette.atomic.borderLight,
          }}
        >
          <SegmentSelect
            isLoading={isLoading}
            segments={params.segments ?? []}
            setSegments={handleSegmentChange}
            columns={segmentColumns}
          />
        </Box>
      )}

      {showControls.includes(PivotControl.Measure) && params.measure && (
        <Box
          sx={{
            px: 2,
            py: "14px",
            borderBottom: "1px solid",
            borderColor: (t) => t.palette.atomic.borderLight,
          }}
        >
          <Measure
            isLoading={isLoading}
            measure={params.measure}
            setMeasure={(measure) => setParams({ ...params, measure })}
            columns={measureColumns}
            disabled={editMode}
          />
        </Box>
      )}

      {showControls.includes(PivotControl.PostFilterByHardcodedColumns) &&
        viewControllerConfig.hardcodedColumns && (
          <Box
          sx={{
            px: 2,
            py: "14px",
            borderBottom: "1px solid",
            borderColor: (t) => t.palette.atomic.borderLight,
          }}
        >
            <FilterBy
              label="Focus Filters"
              isLoading={isLoading}
              excludedFilterColumnFilter={[]}
              columns={viewControllerConfig.hardcodedColumns}
              setViewFilters={(filters) =>
                setParams({ ...params, postFilters: filters })
              }
              viewFilters={params.postFilters ?? []}
            />
          </Box>
        )}

      {showControls.includes(PivotControl.FilterBy) && (
        <Box
          sx={{
            px: 2,
            py: "14px",
            borderBottom: "1px solid",
            borderColor: (t) => t.palette.atomic.borderLight,
          }}
        >
          <FilterBy
            isLoading={isLoading}
            excludedFilterColumnFilter={excludedFilterColumnFilter}
            viewFilters={params.filters}
            setViewFilters={(filters) => setParams({ ...params, filters })}
            columns={userFilterColumns}
          />
        </Box>
      )}

      {showControls.includes(PivotControl.EditModeToggle) && (
        <Box
          sx={{
            px: 2,
            py: "14px",
            borderBottom: "1px solid",
            borderColor: (t) => t.palette.atomic.borderLight,
          }}
        >
          <Box sx={CONTROL_LABEL_SX}>Edit mode</Box>
          <EditModeToggle />
        </Box>
      )}
    </Box>
  );

  // Derive effective controls based on the active status tab.
  let effectiveShowControls = showControls;
  try {
    const tid = (viewControllerConfig as any)?.transformId;
    if (Array.isArray(params?.filters)) {
      const statusFilter = params.filters.find((f: any) => f?.[0] === "status");
      const currentStatus = statusFilter
        ? String(statusFilter[2]?.[0] || "")
        : "";
      // supply_walk_editable: CSV Download only makes sense on the Approved tab.
      if (tid === "supply_walk_editable" && currentStatus !== "Approved") {
        effectiveShowControls = effectiveShowControls.filter(
          (c: any) => c !== PivotControl.Download
        );
      }
      // PO PDFs are rendered from approved_pos, so the PDF button only applies to
      // the Approved tab (goodchop). Hide it elsewhere so it can't be clicked into
      // an empty export.
      if (currentStatus !== "Approved") {
        effectiveShowControls = effectiveShowControls.filter(
          (c: any) => c !== PivotControl.DownloadPdf
        );
      }
    }
  } catch (_) {}

  // Check if the view has expandable rows
  // On the chunked/backend export path the matrix always has group+detail rows
  const hasExpandableRows = onBackendExport
    ? true
    : parsedData
      ? checkIfViewHasExpandableRows(
          parsedData.arrowTable ?? inMemoryArrowTable,
          params ?? null,
          viewControllerConfig,
          columnSchema,
          sortConfig.map(({ key, direction, index }) => ({
            key,
            direction,
            index,
          }))
        )
      : false;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        minHeight: 0,
      }}
    >
      {actionsSlot &&
        createPortal(
          <ControlsHeader
            isLoading={isLoading}
            isExporting={isExporting}
            showControls={effectiveShowControls}
            params={params}
            setParams={setParams}
            datasetName={datasetName}
            transformId={transformId}
            handleDownload={handleDownload}
            handleDownloadPdf={handleDownloadPdf}
            isChartView={isChartView}
            setIsChartView={setIsChartView}
            hasExpandableRows={hasExpandableRows}
          />,
          actionsSlot
        )}

      {/* View-specific custom control panels keep their full-width layout above the table */}
      {customControlPanel}

      {viewControllerConfig.noteText && (
        <Box sx={{ px: 1, pt: 1, pb: 0.5 }}>
          <Typography
            variant="body2"
            sx={{ fontStyle: "italic", color: "text.secondary" }}
          >
            {viewControllerConfig.noteText}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flexGrow: 1,
          minHeight: 0,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        {!CustomPanel && hasSidePanelControls(showControls) && (
          <ControlsSidePanel selections={selections}>
            {sidePanelSections}
          </ControlsSidePanel>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            minWidth: 0,
            minHeight: 0,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};
